// @ts-nocheck - untyped
import React, { useState } from 'react';
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import SmartScanningProxyService from '../../../Services/SmartScanningProxyService';
import Notification from '../../Layout/Notification';
import '../../../App.scss';
import './SmartScanningProxy.scss';

function trimValue(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function validate(values) {
  const errors = {};
  if (!trimValue(values.tenantKey)) errors.tenantKey = 'Tenant Key is required.';
  if (!trimValue(values.dataset)) errors.dataset = 'Dataset is required.';
  if (!trimValue(values.label)) errors.label = 'Label is required.';
  return errors;
}

export default function SmartScanningProxyOnboarding() {
  const [values, setValues] = useState({ tenantKey: '', dataset: '', label: '' });
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [successData, setSuccessData] = useState(null);

  const handleChange = field => e => {
    const v = e.target.value;
    setValues(prev => ({ ...prev, [field]: v }));
    setApiError(null);
    if (errors[field]) {
      const msg = validate({ ...values, [field]: v })[field];
      setErrors(prev => ({ ...prev, [field]: msg || '' }));
    }
  };

  const handleBlur = field => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const nextErrors = validate({ ...values });
    setErrors(prev => ({ ...prev, [field]: nextErrors[field] || '' }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setApiError(null);
    setSuccessData(null);

    const trimmed = {
      tenantKey: trimValue(values.tenantKey),
      dataset: trimValue(values.dataset),
      label: trimValue(values.label),
    };
    const nextErrors = validate(trimmed);

    if (Object.keys(nextErrors).some(k => nextErrors[k])) {
      setErrors(nextErrors);
      setTouched({ tenantKey: true, dataset: true, label: true });
      return;
    }

    setSubmitting(true);
    try {
      const response = await SmartScanningProxyService.createTwinDolphinCustomer(trimmed);
      setSuccessData(response.data);
      setValues({ tenantKey: '', dataset: '', label: '' });
      setTouched({});
      setErrors({});
      Notification.showSuccess('TwinDolphin customer created successfully.');
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.message || err.response?.data || err.message;
      if (status === 409) {
        setApiError('Tenant key already exists.');
      } else {
        setApiError(
          typeof message === 'string' ? message : 'Failed to create customer. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    !submitting &&
    trimValue(values.tenantKey) &&
    trimValue(values.dataset) &&
    trimValue(values.label);

  return (
    <div id="twindolphin-onboarding-form-container">
      <div className="ipxc-admin-body">
        <form onSubmit={handleSubmit} autoComplete="off">
          <Grid container spacing={2} alignItems="stretch">
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" className="ssp-form-card">
                <CardHeader
                  title="Create SMSC client"
                  subheader="Enter tenant key, dataset and label. All fields are required."
                  titleTypographyProps={{ variant: 'h4', fontWeight: 'bold' }}
                  subheaderTypographyProps={{ variant: 'body2' }}
                />
                <CardContent>
                  {successData && (
                    <Alert
                      severity="success"
                      className="ssp-alert-mb"
                      onClose={() => setSuccessData(null)}
                    >
                      Created: <strong>{successData.tenantKey}</strong> (ID: {successData.id})
                    </Alert>
                  )}
                  {apiError && (
                    <Alert
                      severity="error"
                      className="ssp-alert-mb"
                      onClose={() => setApiError(null)}
                    >
                      {apiError}
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    size="small"
                    label="Tenant Key"
                    value={values.tenantKey}
                    onChange={handleChange('tenantKey')}
                    onBlur={handleBlur('tenantKey')}
                    error={Boolean(touched.tenantKey && errors.tenantKey)}
                    helperText={touched.tenantKey && errors.tenantKey}
                    placeholder="e.g. unycom"
                    disabled={submitting}
                    className="ssp-field-mb"
                    inputProps={{ 'data-testid': 'twindolphin-tenant-key' }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Dataset"
                    value={values.dataset}
                    onChange={handleChange('dataset')}
                    onBlur={handleBlur('dataset')}
                    error={Boolean(touched.dataset && errors.dataset)}
                    helperText={touched.dataset && errors.dataset}
                    placeholder="e.g. pair"
                    disabled={submitting}
                    className="ssp-field-mb"
                    inputProps={{ 'data-testid': 'twindolphin-dataset' }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Label"
                    value={values.label}
                    onChange={handleChange('label')}
                    onBlur={handleBlur('label')}
                    error={Boolean(touched.label && errors.label)}
                    helperText={touched.label && errors.label}
                    placeholder="e.g. Unycom_Label"
                    disabled={submitting}
                    className="ssp-field-mb-lg"
                    inputProps={{ 'data-testid': 'twindolphin-label' }}
                  />

                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <div className="ssp-info-box" style={{ flex: 1, marginBottom: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Tenant Key must be unique. If you see &quot;Tenant key already exists&quot;,
                        choose another key.
                      </Typography>
                    </div>

                    <LoadingButton
                      type="submit"
                      variant="contained"
                      loading={submitting}
                      disabled={!canSubmit}
                      data-testid="twindolphin-submit"
                      startIcon={<PersonAddOutlinedIcon />}
                      sx={{ flexShrink: 0 }}
                    >
                      {submitting ? 'Creating...' : 'Create customer'}
                    </LoadingButton>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" className="ssp-side-card">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Process Information
                  </Typography>
                  <Stack spacing={1}>
                    <Alert severity="info" icon={<InfoOutlinedIcon />}>
                      <Typography variant="body2">
                        <strong>Tenant Key:</strong> Unique identifier for the client in SMSC. Must
                        not already exist. Use a short code (e.g. company or product name).
                      </Typography>
                    </Alert>
                    <Alert severity="info" icon={<InfoOutlinedIcon />}>
                      <Typography variant="body2">
                        <strong>Dataset:</strong> Dataset name used for message handling (e.g.
                        &quot;pair&quot;). Must match downstream system expectations.
                      </Typography>
                    </Alert>
                    <Alert severity="info" icon={<InfoOutlinedIcon />}>
                      <Typography variant="body2">
                        <strong>Label:</strong> Display label for the customer (e.g. company label).
                        Used for identification in the system.
                      </Typography>
                    </Alert>
                    <Alert severity="warning" icon={<WarningAmberIcon />}>
                      <Typography variant="body2">
                        <strong>Important:</strong> Duplicate tenant keys are rejected. If creation
                        fails with &quot;Tenant key already exists&quot;, use a different tenant key
                        or contact support to check existing configurations.
                      </Typography>
                    </Alert>
                  </Stack>

                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    fontWeight="bold"
                    className="ssp-tips-title"
                  >
                    Tips for Best Results
                  </Typography>
                  <Typography variant="body2" component="div">
                    <ul className="ssp-tips-list">
                      <li>Use a unique tenant key per client (e.g. company or product code)</li>
                      <li>Ensure dataset and label match values expected by downstream systems</li>
                      <li>Do not create duplicate tenant keys; the system will reject them</li>
                      <li>Keep a record of created configurations for reference</li>
                      <li>If you see a conflict error, verify the key is not already in use</li>
                    </ul>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
}
